import React from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import ActivePaymentTickets from './components/payments-active/ActivePaymentTickets'
import FinishedPaymentTickets from './components/payments-finished/FinishedPaymentTickets'
import ActivePayoutTickets from './components/payouts-active/ActivePayoutTickets'
import FinishedPayoutTickets from './components/payouts-finished/FinishedPayoutTickets'
import VirtualMerchant from './components/virtual-merchant'
import { OverpaymentsPage } from './routes/Overpayments'
import { DisputesPage } from './routes/Disputes'
import { MatchingDashboardPage } from './routes/Dashboard'
import { DepositPayoutTimelinePage } from './routes/DepositPayoutTimeline'
import { MatchingChartsPage } from './routes/Charts'
import { ROLE } from '../auth'
import { PrivateRoute } from '../auth'
import { FlowControlRefusalCardPage, MatchingPayoutCardPage, MatchingTicketCardPage } from '../ticket-card'
import PayoutRefusalsPage from './routes/PayoutRefusals'

const adminManagerRoles = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
]

// CPAYMENT-1690: журнал диспутов и закрытие спора открыты роли, которая фактически разгребает
// очередь FILES_VERIFICATION. Список отдельный, а не расширенный adminManagerRoles: тем же массивом
// гейтится виртуальный мерчант — его роли не открывали.
// CPAYMENT-1732: добавлен менеджер мерчантов — он идёт в паре с менеджером ПС, его выборка сужена
// своими мерчантами на бэкенде (DisputeService::scopedMerchantIds).
const disputeRoles = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
  ROLE.ROLE_MERCHANT_MANAGER,
]

// CPAYMENT-1814: дашборд состояния мэтчинга открыт оператору платежей мерчанта — единственный
// раздел, где эта роль видит картину по всем мерчантам ПС, а не только по своим. Список отдельный,
// а не расширенный adminManagerRoles: тем же массивом гейтятся виртуальный мерчант и переоплаты —
// их роли не открывали. Совпадает с бэкендом (MatchingDashboardController + security.yaml).
const dashboardRoles = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
]

const matchingRoles = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MATCHING_OPERATOR,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER,
]

// CPAYMENT-1800: журнал переоплат открыт оператору платежей мерчанта — создавать переоплаты он умел
// с CPAYMENT-1726, но не видел ни их статус, ни цепочку «исходная выплата → проблемный депозит →
// компенсация». Выборка сужена на бэкенде своими мерчантами по ЛЮБОЙ из сторон записи (исходная
// выплата ИЛИ проблемный депозит), поэтому запись, где его клиент — «виновник», роль тоже видит.
// Список отдельный, а не расширенный adminManagerRoles: тем же массивом гейтится виртуальный
// мерчант, его роли не открывали. Справочник причин /settings/overpayment-reasons роли закрыт.
// CPAYMENT-1835: ROLE_MERCHANT_MANAGER добавлена вместе с действием «Оплатить через локальный
// адаптер» — журнал ей до этого был закрыт вовсе, а выбирать кассу она обязана из него же.
// Выборка сужена своими мерчантами на бэкенде (OverpaymentScope).
const overpaymentJournalRoles = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
  ROLE.ROLE_MERCHANT_MANAGER,
]

const MatchingPage: React.FC = () => {
  return (
    <Routes>
      <Route element={<Outlet />}>
        <Route
          path='payment/active'
          element={
            <PrivateRoute roles={matchingRoles}>
              <ActivePaymentTickets />
            </PrivateRoute>
          }
        />
        <Route
          path='payment/finished'
          element={
            <PrivateRoute roles={matchingRoles}>
              <FinishedPaymentTickets />
            </PrivateRoute>
          }
        />
        {/* Разбор решения мэтчинга. Живёт в матчинговом направлении: мэтчинг уезжает в свой
            сервис и заберёт этот экран с собой, тикетная карточка знает о нём только ссылку. */}
        <Route
          path='payment/card/:token'
          element={
            <PrivateRoute roles={matchingRoles}>
              <MatchingTicketCardPage />
            </PrivateRoute>
          }
        />
        {/* Карточка выплаты в матчинговом разделе: её депозиты и действия, роли — как у списков. */}
        <Route
          path='payout/card/:token'
          element={
            <PrivateRoute roles={matchingRoles}>
              <MatchingPayoutCardPage />
            </PrivateRoute>
          }
        />
        <Route
          path='payout/active'
          element={
            <PrivateRoute roles={matchingRoles}>
              <ActivePayoutTickets />
            </PrivateRoute>
          }
        />
        <Route
          path='payout/finished'
          element={
            <PrivateRoute roles={matchingRoles}>
              <FinishedPayoutTickets />
            </PrivateRoute>
          }
        />
        <Route
          path='payout/refusals'
          element={
            <PrivateRoute roles={matchingRoles}>
              <PayoutRefusalsPage />
            </PrivateRoute>
          }
        />
        <Route
          path='payout/refusal/:id'
          element={
            <PrivateRoute roles={matchingRoles}>
              <FlowControlRefusalCardPage />
            </PrivateRoute>
          }
        />
        <Route
          path='virtual-merchant'
          element={
            <PrivateRoute roles={adminManagerRoles}>
              <VirtualMerchant />
            </PrivateRoute>
          }
        />
        <Route
          path='overpayments'
          element={
            <PrivateRoute roles={overpaymentJournalRoles}>
              <OverpaymentsPage />
            </PrivateRoute>
          }
        />
        <Route
          path='disputes'
          element={
            <PrivateRoute roles={disputeRoles}>
              <DisputesPage />
            </PrivateRoute>
          }
        />
        <Route
          path='dashboard'
          element={
            <PrivateRoute roles={dashboardRoles}>
              <MatchingDashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path='dashboard/timeline'
          element={
            <PrivateRoute roles={dashboardRoles}>
              <DepositPayoutTimelinePage />
            </PrivateRoute>
          }
        />
        <Route
          path='dashboard/charts'
          element={
            <PrivateRoute roles={dashboardRoles}>
              <MatchingChartsPage />
            </PrivateRoute>
          }
        />
        <Route index element={<Navigate to='payment/active' />} />
      </Route>
    </Routes>
  )
}

export default MatchingPage
